import { hexToHslTriplet } from "./partnerConfig";

const STYLE_TAG_ID = "hrx-partner-theme";
const FONT_LINK_ID = "hrx-partner-font-link";
const CUSTOM_CSS_LINK_ID = "hrx-partner-custom-css";
// Stamped on every light-DOM widget host by index.js's mountApp, so mounts
// with a host id of their own (initNucarNext("some-other-id")) get themed
// too — the two literal ids below never matched those, and they rendered in
// default Nucar colors instead of the partner's. Custom properties inherit
// through the shadow boundary, so setting them on the host is enough to
// theme the widget rendered inside it.
export const WIDGET_ROOT_ATTR = "data-hrx-widget-root";

const SCOPE = `#nucar-next-widget, #root, [${WIDGET_ROOT_ATTR}]`;

// The custom properties the current partner theme sets, kept around for
// widget instances a document-level stylesheet can't reach: the modal in
// lib/widgetModal.js mounts the widget in a shadow root *nested* inside the
// overlay's own, and index.css's `:root, :host` defaults land directly on
// that inner shadow host — beating anything merely inherited from above it.
// Applying these as inline styles on that host wins instead (an element's
// own inline style always beats a `:host` rule from inside its shadow tree).
let currentThemeVars = {};

export function getPartnerThemeVars() {
  return currentThemeVars;
}

// Maps nextTheme.colors field names to the CSS custom property they drive.
const COLOR_VAR_MAP = {
  background: "--background",
  foreground: "--foreground",
  card: "--card",
  cardForeground: "--card-foreground",
  popover: "--popover",
  popoverForeground: "--popover-foreground",
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  destructive: "--destructive",
  destructiveForeground: "--destructive-foreground",
  border: "--border",
  input: "--input",
  ring: "--ring",
  brandPrimary: "--nucar-navy",
  brandPrimaryLight: "--nucar-navy-light",
  brandAccent: "--nucar-yellow",
  brandAccentDark: "--nucar-yellow-dark",
};

function removeIfExists(id) {
  document.getElementById(id)?.remove();
}

function buildFontFace(files = []) {
  return files
    .filter((f) => f?.asset?.url)
    .map((f) => {
      const name = f.asset.originalFilename || "";
      const format = name.split(".").pop()?.toLowerCase();
      const formatHint = format === "otf" ? "opentype" : format === "ttf" ? "truetype" : format || "woff2";
      return `
@font-face {
  font-family: "PartnerCustomFont";
  src: url("${f.asset.url}") format("${formatHint}");
  font-display: swap;
}`;
    })
    .join("\n");
}

// Injects a single idempotent <style> tag applying the partner's theme as
// CSS custom property overrides, scoped to the widget/dev-root containers
// (not bare :root) so common var names like --primary don't leak onto the
// host page. No-ops when theme is null/undefined (the default-branding path).
export function applyBrandTheme(theme) {
  removeIfExists(STYLE_TAG_ID);
  removeIfExists(FONT_LINK_ID);
  removeIfExists(CUSTOM_CSS_LINK_ID);

  currentThemeVars = {};

  if (!theme) return;

  const vars = {};

  Object.entries(theme.colors || {}).forEach(([key, hex]) => {
    const cssVar = COLOR_VAR_MAP[key];
    const hsl = hexToHslTriplet(hex);
    if (cssVar && hsl) vars[cssVar] = hsl;
  });

  if (theme.radius) vars["--radius"] = theme.radius;

  const hasCustomFont = theme.fonts?.customFont?.files?.length > 0;
  if (theme.fonts?.headingFamily) {
    vars["--font-heading"] = theme.fonts.headingFamily;
  } else if (hasCustomFont) {
    vars["--font-heading"] = '"PartnerCustomFont", sans-serif';
  }
  if (theme.fonts?.bodyFamily) {
    vars["--font-body"] = theme.fonts.bodyFamily;
  } else if (hasCustomFont) {
    vars["--font-body"] = '"PartnerCustomFont", sans-serif';
  }

  currentThemeVars = vars;
  const declarations = Object.entries(vars).map(([name, value]) => `  ${name}: ${value};`);

  if (theme.fonts?.googleFontsUrl) {
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = theme.fonts.googleFontsUrl;
    document.head.appendChild(link);
  }

  const fontFaceBlock = hasCustomFont ? buildFontFace(theme.fonts.customFont.files) : "";

  if (declarations.length > 0 || fontFaceBlock) {
    const style = document.createElement("style");
    style.id = STYLE_TAG_ID;
    style.textContent = `${fontFaceBlock}\n${SCOPE} {\n${declarations.join("\n")}\n}`;
    document.head.appendChild(style);
  }

  if (theme.custom_css_link) {
    const link = document.createElement("link");
    link.id = CUSTOM_CSS_LINK_ID;
    link.rel = "stylesheet";
    link.href = theme.custom_css_link;
    document.head.appendChild(link);
  }
}
